import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import throttle from 'lodash.throttle';

import { Link } from 'gatsby';

import SocialButtons from '../social-buttons';
import Nav from './nav';
import ThemeToggle from './theme-toggle';
import HeaderToggle from './header-toggle';

const StyledThemeToggle = styled(ThemeToggle)`
  grid-area: theme-toggle;
`;

const StyledTitleLink = styled(Link)`
  grid-area: title;
  text-align: center;
`;

const StyledSocialButtons = styled(SocialButtons)`
  grid-area: social;
`;

const StyledNav = styled(Nav)`
  grid-area: nav;
  max-width: min-content;
  justify-self: center;
`;

const StyledPrivacyLink = styled(Link)`
  grid-area: privacy;
`;

const StyledHeaderToggle = styled(HeaderToggle)`
  grid-area: menu-toggle;

  @media (min-width: 900px) {
    display: none;
    visibility: hidden;
  }
`;

const StyledHiddenMenuToggle = styled(HeaderToggle)`
  position: fixed;
  right: var(--spacer);
  bottom: var(--spacer);
  z-index: 2;

  @media (min-width: 900px) {
    display: none;
    visibility: hidden;
  }
`;

const HeaderWrapper = styled.div`
  grid-area: header;
  padding: var(--spacer);
  display: grid;
  gap: var(--spacer);
  margin-bottom: 0;

  justify-content: center;
  justify-items: center;
  align-items: center;

  grid-template-columns: min-content 1fr min-content;
  overflow-y: auto;
  overflow-x: hidden;

  grid-template-areas: "menu-toggle title theme-toggle";

  @media (max-width: 900px) {
    ${StyledSocialButtons}, ${StyledNav}, ${StyledPrivacyLink} {
      ${({ isCollapsed }) => (isCollapsed ? 'display: none;' : '')}
    }
  }

  visibility: var(--visibility);

  position: sticky;
  top: 0;
  z-index: 1;

  background: var(--glass-bg);
  box-shadow: var(--glass-glow);
  border: var(--glass-border);
  backdrop-filter: var(--glass-filter);

  backdrop-filter: blur(6.5px);

  &.open {
    grid-template-areas:
      "menu-toggle title theme-toggle"
      "social social social"
      "nav nav nav"
      "privacy privacy privacy";

    grid-template-rows: min-content auto auto min-content;

    position: fixed;
    width: 100vw;
    height: 100vh;
  }

  @media (min-width: 900px) {
    grid-template-areas:
      "theme-toggle"
      "title"
      "social"
      "nav"
      "privacy";

    grid-template-columns: 1fr;
    justify-items: center;
    align-content: center;

    visibility: visible;

    height: 100vh;
  }
`;

export default function Header({ nav, privacy }) {
  const lastScroll = useRef(0);
  const [isMenuHidden, setHideMenu] = useState(false);
  const [isCollapsed, setCollapsed] = useState(true);

  function handleScroll() {
    if (!lastScroll.current) lastScroll.current = 0;

    const currentScroll = window.pageYOffset;
    const visible = lastScroll.current < currentScroll;

    setHideMenu(visible);
    lastScroll.current = currentScroll;
  }

  useEffect(() => {
    const throttled = throttle(handleScroll, 100, { leading: true });

    window.addEventListener('scroll', throttled);

    return () => {
      window.removeEventListener('scroll', throttled);
    };
  }, []);

  useEffect(() => {
    if (isCollapsed) document.body.classList.remove('overflow');
    else document.body.classList.add('overflow');
  }, [isCollapsed]);

  return (
    <>
      <HeaderWrapper
        className={!isCollapsed ? 'open' : ''}
        isCollapsed={isCollapsed}
        style={{
          '--visibility': isMenuHidden ? 'collapse' : 'visible',
        }}
      >
        <StyledPrivacyLink to="/privacy">Datenschutz</StyledPrivacyLink>

        <StyledSocialButtons />

        {Object.keys(nav).length > 0 && <StyledNav nav={nav} />}

        <StyledTitleLink to="/">
          <h3>Roman Kollatschny</h3>
        </StyledTitleLink>

        <StyledThemeToggle />

        <StyledHeaderToggle
          onClick={() => {
            setHideMenu(false);
            setCollapsed((b) => !b);
          }}
        />

      </HeaderWrapper>
      {isMenuHidden && (
        <StyledHiddenMenuToggle
          onClick={() => {
            setHideMenu(false);
          }}
        />
      )}
    </>
  );
}
